import { z } from "zod";

import type { AvailabilityResponse } from "@/shared";
import type { OnlineSchedulingInsuranceCardResponse } from "@/src/lib/scheduling/insurance-card-extraction";
import {
  onlineSchedulingAppointmentsResponseSchema,
  onlineSchedulingAvailabilityResponseSchema,
  onlineReschedulingResponseSchema,
  type OnlineSchedulingAppointmentsRequest,
  type OnlineReschedulingAvailabilityRequest,
  type OnlineReschedulingRequest,
  type OnlineSchedulingAvailabilityRequest,
  type OnlineSchedulingPatientOptionsRequest,
  type OnlineSchedulingEligibilityResponse,
  type OnlineSchedulingBookRequest,
  type OnlineSchedulingBookResponse,
  type OnlineSchedulingOptionsResponse,
} from "@/src/lib/scheduling/online-scheduling-schemas";

/**
 * Browser-side fetch helpers and response validation for the online scheduling API.
 * These import no server modules and are safe to use from client components.
 */

const BASE = "/api/online-scheduling";

export class OnlineSchedulingRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string
  ) {
    super(message);
    this.name = "OnlineSchedulingRequestError";
  }
}

export async function fetchOnlineSchedulingAppointments(
  request: OnlineSchedulingAppointmentsRequest,
  tenant?: string | null
) {
  return onlineSchedulingAppointmentsResponseSchema.parse(
    await fetchJson<unknown>("/appointments", tenant, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    })
  );
}

export async function fetchOnlineReschedulingAvailability(
  request: OnlineReschedulingAvailabilityRequest,
  tenant?: string | null
) {
  return onlineSchedulingAvailabilityResponseSchema.parse(
    await fetchJson<unknown>("/reschedule/availability", tenant, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    })
  );
}

export async function rescheduleOnlineSchedulingAppointment(
  request: OnlineReschedulingRequest,
  tenant?: string | null
) {
  return onlineReschedulingResponseSchema.parse(
    await fetchJson<unknown>("/reschedule", tenant, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    })
  );
}

function withTenant(path: string, tenant?: string | null): string {
  return tenant ? `${path}${path.includes("?") ? "&" : "?"}tenant=${encodeURIComponent(tenant)}` : path;
}

/** Applies the optional `?tenant` override and surfaces the API's error message. */
async function fetchJson<T>(path: string, tenant: string | null | undefined, init?: RequestInit): Promise<T> {
  const response = await fetch(withTenant(`${BASE}${path}`, tenant), init);

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    let code: string | undefined;

    try {
      const data: unknown = await response.json();

      const parsed = z
        .object({ error: z.string().optional(), message: z.string().optional(), code: z.string().optional() })
        .safeParse(data);

      if (parsed.success) {
        message = parsed.data.error ?? parsed.data.message ?? message;
        code = parsed.data.code;
      }
    } catch {
      // response had no JSON body; keep the status-based message
    }

    throw new OnlineSchedulingRequestError(message, response.status, code);
  }

  return (await response.json()) as T;
}

export async function fetchOnlineSchedulingOptions(
  tenant?: string | null,
  regionCode?: string | null
): Promise<OnlineSchedulingOptionsResponse> {
  const path = regionCode ? `/options?regionCode=${encodeURIComponent(regionCode)}` : "/options";

  return fetchJson<OnlineSchedulingOptionsResponse>(path, tenant, { method: "GET" });
}

export async function fetchOnlineSchedulingAvailability(
  query: OnlineSchedulingAvailabilityRequest,
  tenant?: string | null
): Promise<AvailabilityResponse> {
  return fetchJson<AvailabilityResponse>("/availability", tenant, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(query),
  });
}

export async function bookOnlineSchedulingAppointment(
  request: OnlineSchedulingBookRequest,
  tenant?: string | null
): Promise<OnlineSchedulingBookResponse> {
  return fetchJson<OnlineSchedulingBookResponse>("/book", tenant, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
}

export async function checkOnlineSchedulingEligibility(
  request: OnlineSchedulingPatientOptionsRequest,
  tenant?: string | null
): Promise<OnlineSchedulingEligibilityResponse> {
  return fetchJson<OnlineSchedulingEligibilityResponse>("/eligibility", tenant, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
}

export async function readOnlineSchedulingInsuranceCard(
  files: File[],
  tenant?: string | null
): Promise<OnlineSchedulingInsuranceCardResponse> {
  const form = new FormData();

  for (const file of files) form.append("images", file);

  return fetchJson<OnlineSchedulingInsuranceCardResponse>("/insurance-card", tenant, {
    method: "POST",
    body: form,
    signal: AbortSignal.timeout(30_000),
  });
}
